import React from "react";
import { lazy, Suspense } from "react";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import { AdminProtectedRoute } from "@/components/AdminProtectedRoute";
import { EdgeFunctionLoadingIndicator } from "@/components/EdgeFunctionLoadingIndicator";
import { EmailBounceNotice } from "@/components/EmailBounceNotice";
import { KeyboardShortcutsDialog } from "@/components/KeyboardShortcutsDialog";
import { KeyboardShortcutsIndicator } from "@/components/KeyboardShortcutsIndicator";
import { CommunicationCodeMigrationBanner } from "@/components/onboarding/CommunicationCodeMigrationBanner";
import { CommunicationCodeOnboarding } from "@/components/onboarding/CommunicationCodeOnboarding";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import SentryErrorBoundary from "@/components/SentryErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth , AuthProvider } from "@/contexts/AuthContext";
import { RealtimeProvider } from "@/contexts/RealtimeContext";
import { useSentryNavigation } from "@/hooks/useSentryBreadcrumbs";


import Index from "./pages/Index";

// Lazy-loaded route components for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const Waitlist = lazy(() => import("./pages/Waitlist"));
const AdminPanel = lazy(() => import("./pages/AdminPanel"));
const AdminIPManagement = lazy(() => import("./pages/AdminIPManagement"));
const PromptManagement = lazy(() => import("./pages/PromptManagement"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const VerifyEmail = lazy(() => import("./pages/VerifyEmail"));
const VerifyMagicLink = lazy(() => import("./pages/VerifyMagicLink"));
const BoardDetail = lazy(() => import("./pages/BoardDetail"));
const BoardCreationWizard = lazy(() => import("./pages/BoardCreationWizard"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const Projects = lazy(() => import("./pages/Projects"));
const SessionChat = lazy(() => import("./pages/SessionChat"));
const Search = lazy(() => import("./pages/Search"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MemoriesOverview = lazy(() => import("./pages/MemoriesOverview"));
const QuickStartGuide = lazy(() => import("./pages/QuickStartGuide"));
const OnboardingAnalytics = lazy(() => import("./pages/OnboardingAnalytics"));
const Takeout = lazy(() => import("./pages/Takeout"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Profile = lazy(() => import("./pages/Profile"));
const NotificationHistory = lazy(() => import("./pages/NotificationHistory"));
const Tests = lazy(() => import("./pages/Tests"));

const queryClient = new QueryClient();

const AppContent = () => {
  useSentryNavigation(); // Track navigation for Sentry breadcrumbs
  const { user } = useAuth();
  
  return (
    <>
      <EdgeFunctionLoadingIndicator />
      <KeyboardShortcutsDialog />
      <KeyboardShortcutsIndicator />
      <CommunicationCodeOnboarding />
      <CommunicationCodeMigrationBanner />
      {user && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-4">
          <EmailBounceNotice />
        </div>
      )}
      <Suspense
        fallback={
          <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="text-center space-y-4">
              <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </div>
        }
      >
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/verify-magic-link" element={<VerifyMagicLink />} />
          <Route path="/waitlist" element={<Waitlist />} />
          <Route path="/tests" element={<Tests />} />
          
          {/* Protected routes - require authentication and non-waitlisted status */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/admin" element={<AdminProtectedRoute><AdminPanel /></AdminProtectedRoute>} />
          <Route path="/admin/ip-management" element={<AdminProtectedRoute><AdminIPManagement /></AdminProtectedRoute>} />
          <Route path="/admin/prompt-management" element={<AdminProtectedRoute><PromptManagement /></AdminProtectedRoute>} />
          <Route path="/boards/create" element={<ProtectedRoute><BoardCreationWizard /></ProtectedRoute>} />
          <Route path="/boards/:boardId" element={<ProtectedRoute><BoardDetail /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute><Projects /></ProtectedRoute>} />
          <Route path="/projects/:projectId" element={<ProtectedRoute><ProjectDetail /></ProtectedRoute>} />
          <Route path="/sessions/:sessionId" element={<ProtectedRoute><SessionChat /></ProtectedRoute>} />
          <Route path="/search" element={<ProtectedRoute><Search /></ProtectedRoute>} />
          <Route path="/memories" element={<ProtectedRoute><MemoriesOverview /></ProtectedRoute>} />
          <Route path="/quick-start" element={<ProtectedRoute><QuickStartGuide /></ProtectedRoute>} />
          <Route path="/admin/onboarding-analytics" element={<AdminProtectedRoute><OnboardingAnalytics /></AdminProtectedRoute>} />
          <Route path="/takeout" element={<ProtectedRoute><Takeout /></ProtectedRoute>} />
          <Route path="/achievements" element={<ProtectedRoute><Achievements /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><NotificationHistory /></ProtectedRoute>} />
          
          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="system" storageKey="advisor-muse-theme">
      <TooltipProvider>
        <Toaster />
        <BrowserRouter>
          <SentryErrorBoundary 
            context={{ 
              component: "AuthProvider",
              additionalData: { critical: true }
            }}
          >
            <AuthProvider>
              <RealtimeProvider>
                <AppContent />
              </RealtimeProvider>
            </AuthProvider>
          </SentryErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
